import React, { useEffect, useState } from 'react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import baseUrl from '../../config'; // Assuming this correctly points to your base URL

const YourNextClass = () => {
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchClassSchedules = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found. Please log in.');
        }

        const response = await fetch(`${baseUrl}api/students/enrolled-courses`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          // Attempt to read error message from response body
          const errorData = await response.json();
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Ensure data exists and has the expected structure before accessing properties
        // Assuming data is an array and we're interested in the first course's nextClass
        if (data && Array.isArray(data) && data.length > 0 && data[0].course && data[0].course.nextClass) {
          setClassData(data[0].course.nextClass);
        } else {
          setClassData(null); // Explicitly set to null if no next class is found
          console.log("No upcoming class found or data structure is unexpected.");
        }

      } catch (err) {
        console.error("Error fetching class schedules:", err);
        setError(err.message || 'An unexpected error occurred while fetching schedules.');
      } finally {
        setLoading(false);
      }
    };

    fetchClassSchedules();
  }, []); // Empty dependency array means this effect runs once on mount

  // --- Render Logic ---

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-glass border-b-4 border-orange-600 animate-pulse flex flex-col items-center justify-center">
        <div className="w-12 h-12 bg-orange-200 rounded-full mb-4"></div>
        <p className="text-gray-600 font-semibold">Loading your next class...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-2xl p-6 shadow-glass border-b-4 border-red-600 flex flex-col items-center justify-center text-red-800">
        <p className="text-lg font-semibold mb-2">Error!</p>
        <p className="text-center">{error}</p>
        <p className="text-sm mt-2">Please try refreshing the page or contact support.</p>
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-glass border-b-4 border-gray-400 flex flex-col items-center justify-center text-gray-700">
        <CalendarIcon className="w-12 h-12 mb-4 text-gray-500" />
        <h2 className="text-xl md:text-2xl font-bold mb-2">No Upcoming Classes</h2>
        <p className="text-center">Looks like you're all caught up! Check back later for new schedules.</p>
      </div>
    );
  }

  // If we reach here, classData is available
  const classTitle = classData.topics?.[0]?.title || 'Upcoming Class';
  const teacherName = classData.teacherName || 'Unknown Teacher';
  const classDateTime = classData.classDate && classData.time
    ? `${new Date(classData.classDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} - ${classData.time}`
    : 'Date and Time N/A';

  return (
    <div className="bg-white rounded-2xl p-6 shadow-glass border-b-4 border-orange-600 transition-all duration-300 hover:shadow-xl hover:scale-[1.01]">
      <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-5 flex items-center">
        <CalendarIcon className="w-9 h-9 mr-3 text-orange-600 animate-bounce-slow" /> {/* Added a subtle animation */}
        Your Next Class!
      </h2>
      <div className="bg-orange-50 p-5 rounded-2xl text-orange-800 font-semibold text-lg flex items-start space-x-4 shadow-inner border border-orange-200">
        <span className="text-5xl transform rotate-12 transition-transform duration-300">📚</span> {/* Changed emoji */}
        <div>
          <p className="text-xl font-bold text-orange-900 mb-1 leading-tight">{classTitle}</p>
          <p className="text-base text-gray-700 mb-1">with <span className="font-semibold">{teacherName}</span></p>
          <p className="text-base text-gray-700">
            <strong className="text-orange-700">{classDateTime}</strong>
          </p>
        </div>
      </div>
      <button
        onClick={() => {
          if (classData.joinUrl) {
            window.open(classData.joinUrl, '_blank');
          } else {
            alert('Join URL is not available for this class.'); // Provide feedback if URL is missing
          }
        }}
        className="mt-6 w-full bg-orange-600 text-white py-3 rounded-full font-bold text-xl hover:bg-orange-700 transition-all duration-300 ease-in-out transform hover:scale-[1.03] shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-orange-300"
      >
        Join Class Now!
      </button>
    </div>
  );
};

export default YourNextClass;